// ================================================= /
// ======   SUBNAV JS   ============================ /
// ================================================= /

/* eslint-disable max-depth, max-statements */

/* global jQuery */

import { throttle, debounce } from 'throttle-debounce';
import gsap from 'gsap';
import ScrollToPlugin from 'gsap/ScrollToPlugin';

import helpers from './helpers';
import preloader from './preloader';
import animation from './animation';

/**
 * Class for handling the subnav
 */
class DispatchSkinSubnav {

	/**
	 * Constructs the class and sets up instance variables.
	 */
	constructor() {

		gsap.registerPlugin( ScrollToPlugin );

		// Selectors
		this.selector = '.js-subnav';
		this.subnavInnerSelector = `${this.selector}__inner`;
		this.anchorSelector = `${this.selector}__anchor`;
		this.anchorNumberSelector = `${this.anchorSelector}-number`;
		this.hamburgerSelector = `${this.selector}__hamburger`;
		this.headerNavigationSelector = `${this.selector}__header-navigation`;
		this.intrusiveDarkSectionSelector = `${this.selector}__intrusive-dark-section`;
		this.progressSelector = `${this.selector}__progress`;
		this.progressMeterSelector = `${this.progressSelector}-meter`;
		this.fobSelector = `${this.selector}__fob`;
		this.heroSelector = `${this.selector}__hero`;

		// Elements & offsets
		this.subnav = null;
		this.subnavOffset = 0;
		this.subnavInner = null;
		this.subnavInnerOffset = null;
		this.anchors = null;
		this.anchorOffsets = [];
		this.anchorNumbers = null;
		this.anchorNumberOffsets = [];
		this.hamburger = null;
		this.hamburgerOffset = null;
		this.headerNavigation = null;
		this.intrusiveDarkSections = [];
		this.intrusiveDarkSectionOffsets = [];
		this.progress = null;
		this.heroOffset = null;

		// General config
		this.eventNamespace = 'dispatchSkinSubnav';
		this.anchorDataAttribute = 'anchor';
		this.activeClass = 'is-active';
		this.onDarkClass = 'is-on-dark';
		this.doneResizingClass = 'is-done-resizing';
		this.toggledClass = 'is-toggled';
		this.hasToggledNavClass = 'has-toggled-nav';
		this.pastHeroClass = 'is-past-hero';

		this.throttle = 200;
		this.debounce = 250;
		this.intersectionTolerance = 5;

		this.breakpoint = 1024;
		this.scrollDuration = 0.5;

		// Nav animation
		this.navTransform = '-2em';
		this.navAnimationDuration = 0.3;

		// State
		this.lastWindowWidth = window.innerWidth;
		this.isListeningForScroll = false;
		this.isResizing = false;
		this.isMobile = window.innerWidth < this.breakpoint;
		this.isNavOpen = false;
		this.isNavSticky = false;
		this.isPastHero = false;
	}

	/**
	 * Initializes the class, sets up events.
	 */
	initialize() {

		this.subnav = jQuery( this.selector );

		if ( this.subnav.length ) {

			this.subnavInner = jQuery( this.subnavInnerSelector );
			this.anchors = jQuery( this.anchorSelector );
			this.anchorNumberList = jQuery( this.anchorNumberListSelector );
			this.anchorNumbers = jQuery( this.anchorNumberSelector );
			this.hamburger = jQuery( this.hamburgerSelector );
			this.headerNavigation = jQuery( this.headerNavigationSelector );
			this.intrusiveDarkSections = jQuery( this.intrusiveDarkSectionSelector );
			this.progress = jQuery( this.progressSelector );
			this.progressMeter = jQuery( this.progressMeterSelector );
			this.hero = jQuery( this.heroSelector );

			this.getOffsets();
			this.handleScroll();

			this.events();
		}
	}

	/* Setup methods ---------------------------------------- */

	/**
	 * Wires up events.
	 */
	events() {

		const delegator = jQuery( '.site' );
		const jQueryWindow = jQuery( window );

		delegator.on(
			`vclick.${this.eventNamespace}hamburgerClick`,
			this.hamburgerSelector,
			this.handleHamburgerClick.bind( this )
		);

		delegator.on(
			`vclick.${this.eventNamespace}anchorNumberClick`,
			this.anchorNumberSelector,
			this.handleAnchorNumberClick.bind( this )
		);

		this.listenForScroll( ! this.isMobile );

		jQueryWindow.on(
			`resize.${this.eventNamespace}Resize`,
			this.handleResize.bind( this )
		);

		jQueryWindow.on(
			`resize.${this.eventNamespace}Resize`,
			debounce(
				this.debounce,
				this.handleResizeEnd.bind( this )
			)
		);

		// Animation

		jQueryWindow.on(
			preloader.finishEvent,
			() => animation.resolve( this.selector )
		);

		animation.addHook(
			this.selector,
			function( subnav ) {

				gsap.to(
					subnav.find( this.anchorNumberSelector ),
					{
						duration: 0.25,
						delay: 1,
						stagger: 0.025,
						opacity: 1,
						x: 0,
						onComplete: () => gsap.to( subnav.find( this.fobSelector ), { duration: 0.5, ease: 'back.out', scale: 1 } )
					}
				);
			}.bind( this )
		);
	}

	/**
	 * Attaches or detatches the scroll event listener
	 */
	listenForScroll( attach = true ) {

		if ( attach ) {

			jQuery( window ).on(
				`scroll.${this.eventNamespace}Scroll`,
				throttle( this.throttle, this.handleScroll.bind( this ) )
			);
		}

		else {
			jQuery( window ).off( `scroll.${this.eventNamespace}Scroll` );
		}

		this.isListeningForScroll = attach;
	}

	/**
	 * Sets up all of the element offsets.
	 * This can be relative to the document or viewport.
	 */
	getOffsets( elements = ['subnav', 'subnavInner', 'anchors', 'anchorNumbers', 'hamburger', 'hero', 'intrusiveDarkSections'] ) {

		if ( elements.indexOf( 'subnav' ) !== -1 ) {
			this.subnavOffset = this.getElementOffset( this.subnav );
		}

		if ( elements.indexOf( 'subnavInner' ) !== -1 ) {
			this.subnavInnerOffset = this.getElementOffset( this.subnavInner, true );
		}

		if ( elements.indexOf( 'anchors' ) !== -1 ) {
			this.anchorOffsets = [];
			this.anchors.each(
				function( index, anchor ) {
					this.anchorOffsets.push( this.getElementOffset( anchor ) );
				}.bind( this )
			);
		}

		if ( elements.indexOf( 'anchorNumbers' ) !== -1 ) {
			this.anchorNumbers.each(
				function( index, anchorNumber ) {
					this.anchorNumberOffsets.push( this.getElementOffset( anchorNumber, true ) );
				}.bind( this )
			);
		}

		if ( elements.indexOf( 'hamburger' ) !== -1 ) {
			this.hamburgerOffset = this.getElementOffset( this.hamburger, true );
		}

		if ( elements.indexOf( 'hero' ) !== -1 ) {
			this.heroOffset = this.getElementOffset( this.hero );
		}

		if ( elements.indexOf( 'intrusiveDarkSections' ) !== -1 ) {
			this.intrusiveDarkSections.each(
				function( index, intrusiveDarkSection ) {
					this.intrusiveDarkSectionOffsets.push( this.getElementOffset( intrusiveDarkSection ) );
				}.bind( this )
			);
		}
	}

	/**
	 * Gets an element's offset in the correct format.
	 */
	getElementOffset( element, relativeToViewport = false ) {

		element = jQuery( element );

		const offset = element.offset();

		const offsetObject = {
			element: element,
			top: offset.top,
			bottom: offset.top + element.outerHeight()
		};

		if ( relativeToViewport ) {

			const rect = element[0].getBoundingClientRect();

			offsetObject.viewport = {
				top: rect.top,
				bottom: rect.top + element.outerHeight()
			};
		}

		return offsetObject;
	}

	/* Event handlers --------------------------------------- */

	/**
	 * Handles the hamburger click event.
	 */
	handleHamburgerClick() {
		this.toggleNav( ! this.isNavOpen );
	}

	/**
	 * Handles the anchor number click event.
	 */
	handleAnchorNumberClick( event ) {

		gsap.to(
			window,
			{
				duration: this.scrollDuration,
				scrollTo: {
					y: `#${jQuery( event.currentTarget ).data( this.anchorDataAttribute )}`,
					offsetY: this.getWindowEyeLevel().viewport / 2
				}
			}
		);
	}

	/**
	 * Handles the scroll event.
	 */
	handleScroll() {

		const windowTop = window.pageYOffset;
		const windowHeight = window.innerHeight;
		const windowBottom = windowTop + windowHeight;

		const windowEyeLevel = this.getWindowEyeLevel( windowTop, windowHeight );

		this.setStates( windowTop );
		this.setBackgroundClasses( windowTop, windowBottom );

		requestAnimationFrame(
			function() {
				this.setProgress( windowTop, windowEyeLevel.document, windowEyeLevel.viewport, windowHeight );
			}.bind( this )
		);
	}

	/**
	 * Handles resize
	 */
	handleResize() {

		this.isMobile = window.innerWidth < this.breakpoint;

		if ( window.innerWidth !== this.lastWindowWidth ) {

			if ( this.isNavOpen ) {
				this.toggleNav( false, false );
			}
			if ( ! this.isPastHero ) {
				this.toggleNavVisibility( ! this.isMobile, false );
			}
		}

		this.lastWindowWidth = window.innerWidth;

		this.headerNavigation.removeClass( this.doneResizingClass );
		this.getOffsets();
		this.handleScroll();
	}
	handleResizeEnd() {

		this.listenForScroll( ! this.isMobile );
		this.headerNavigation.addClass( this.doneResizingClass );
	}

	/* Handler methods -------------------------------------- */

	/**
	 * Sets states for whole thing.
	 *
	 * Sets whether or not we've scrolled past the hero
	 *
	 * Sets the position of the anchor number list.
	 * Stops moving as a fixed element when it hits the top of the footer.
	 *
	 * TODO: Make sure this works on page load
	 */
	setStates( windowTop ) {

		// See if we've passed the hero

		if ( windowTop > this.heroOffset.bottom ) {

			if ( ! this.isPastHero ) {
				this.stickNav();
			}
		}
		else if ( this.isPastHero ) {

			this.toggleNav(
				false,
				this.isNavOpen,
				() => this.stickNav( false )
			);
		}
	}

	/**
	 * Sets background classes for items on scroll.
	 */
	setBackgroundClasses( windowTop, windowBottom ) {

		// We only care about intrusive dark sections that are in the viewport
		const intrusiveDarkSectionOffsets = this.intrusiveDarkSectionOffsets.filter(
			( offset ) => helpers.doElementOffsetsIntersect( offset, { top: windowTop, bottom: windowBottom }, this.intersectionTolerance )
		);

		if ( intrusiveDarkSectionOffsets.length ) {

			for ( const s of intrusiveDarkSectionOffsets ) {

				// Compare hamburger
				if ( helpers.doElementOffsetsIntersect( { top: this.hamburgerOffset.viewport.top + windowTop, bottom: this.hamburgerOffset.viewport.bottom + windowTop }, s, this.intersectionTolerance ) ) {
					this.hamburger.addClass( this.onDarkClass );
					this.headerNavigation.addClass( this.onDarkClass );
				}
				else {
					this.hamburger.removeClass( this.onDarkClass );
					this.headerNavigation.removeClass( this.onDarkClass );
				}

				// Compare anchor numbers
				for ( const n of this.anchorNumberOffsets ) {

					if ( helpers.doElementOffsetsIntersect( { top: n.viewport.top + windowTop, bottom: n.viewport.bottom + windowTop }, s, this.intersectionTolerance ) ) {
						n.element.addClass( this.onDarkClass );
					}
					else {
						n.element.removeClass( this.onDarkClass );
					}
				}
			}
		}

		else {
			this.anchorNumbers.removeClass( this.onDarkClass );
			this.hamburger.removeClass( this.onDarkClass );
			this.headerNavigation.removeClass( this.onDarkClass );
		}
	}

	/**
	 * Sets the progress.
	 */
	setProgress( windowTop, windowEyeLevel, windowEyeLevelTop, windowHeight ) {

		// Let's assume we haven't scrolled past the first anchor yet
		let activeAnchorIndex = false;
		let activeAnchorOffset = 0;
		let activeAnchorNumber = false;
		let activeAnchorNumberOffset = this.subnavInnerOffset.top;

		// Now we need to determine the first one we have scrolled past
		// Anchors are explicitly added sequentially down the page, so this can break early
		for ( let a = 0; a < this.anchorOffsets.length; a++ ) {

			if ( windowEyeLevel - this.anchorOffsets[a].top < 0 ) {
				break;
			}
			activeAnchorIndex = a;
		}

		const nextAnchorIndex = activeAnchorIndex !== false ? activeAnchorIndex + 1 : 0;
		// let nextAnchorOffset = this.subnavOffset.bottom;
		let nextAnchorOffset = document.body.clientHeight - ( windowHeight - windowEyeLevelTop );
		let nextAnchorNumberOffset = this.subnavInnerOffset.bottom;

		// If we have an active anchor, add the offsets
		if ( activeAnchorIndex !== false ) {
			activeAnchorOffset = this.anchorOffsets[activeAnchorIndex].top;
			activeAnchorNumber = this.anchorNumbers.eq( activeAnchorIndex );
			activeAnchorNumberOffset = this.anchorNumberOffsets[activeAnchorIndex].top;
		}

		// If the next anchor index actually exists, add its offsets
		if ( this.anchors[nextAnchorIndex] ) {

			nextAnchorOffset = this.anchorOffsets[nextAnchorIndex].top;
			nextAnchorNumberOffset = this.anchorNumberOffsets[nextAnchorIndex].top;
		}

		// Set the active number if we have an active anchor
		if ( activeAnchorNumber ) {
			this.anchorNumbers.not( activeAnchorNumber ).removeClass( this.activeClass );
			activeAnchorNumber.addClass( this.activeClass );
		}
		else {
			this.anchorNumbers.removeClass( this.activeClass );
		}

		// This part is kinda complicated, so it's well commented
		// You're welcome
		// — Nick from the past

		// Progress bar height is calculated relative to the height of the subnav inner
		// It will eventually be a transform: scaleY value, so it must be a ratio
		// It's done this way because the progress bar is absolutely positioned within it
		const subnavInnerHeight = this.subnavInnerOffset.bottom - this.subnavInnerOffset.top;

		// We start with a base ratio that is the active number's top offset from the inner
		// This is given as a proportion relative to the subnav inner's height
		const activeAnchorNumberOffsetRatioFromInner = ( activeAnchorNumberOffset - this.subnavInnerOffset.top ) / subnavInnerHeight;

		// Since we're determining the active anchor from where the user's eye would typically be, it makes scrolling to the first anchor kinda weird
		// We have to correct for that since we don't want the progress bar starting from anything but zero

		let windowScrollAdjusted = windowEyeLevel;

		if ( activeAnchorIndex === false ) {
			windowScrollAdjusted = windowEyeLevel * ( windowTop / ( nextAnchorOffset - windowEyeLevelTop ) );
		}

		// Then, need to determine how far we have scrolled past the active anchor and towards the next one
		const scrollPastActiveAnchor = windowScrollAdjusted - activeAnchorOffset;

		// Next, we need to determine how far that scroll is relative to the distance between the two anchors
		const distanceBetweenAnchors = nextAnchorOffset - activeAnchorOffset;
		const scrollBetweenAnchorsRatio = scrollPastActiveAnchor / distanceBetweenAnchors;

		// Now, we need to determine the difference between the anchor numbers
		// This will then be made proportional to the subnav inner's height
		const distanceBetweenAnchorNumbers = nextAnchorNumberOffset - activeAnchorNumberOffset;
		const distanceBetweenAnchorsRatioFromInner = distanceBetweenAnchorNumbers / subnavInnerHeight;

		// Now, we have to apply the scroll ratio between the two anchors to the distance ratio between anchor numbers
		const progressBetweenAnchorsRatioFromInner = distanceBetweenAnchorsRatioFromInner * scrollBetweenAnchorsRatio;

		// The final progress ratio is the initial base of the active anchor number plus the proportional progress towards the next
		const progress = Math.min(
			activeAnchorNumberOffsetRatioFromInner + progressBetweenAnchorsRatioFromInner,
			1
		);

		this.progressMeter.attr( 'value', progress * 100 );
		this.progress.css( 'transform', `perspective(1px) scaleY(${progress})` );
	}

	/* Misc. subnav methods --------------------------------- */

	/**
	 * Sticks and unsticks the nav
	 */
	stickNav( toStick = true ) {
		this.isPastHero = toStick;
		this.subnav[toStick ? 'addClass' : 'removeClass']( this.pastHeroClass );
		this.headerNavigation[toStick ? 'addClass' : 'removeClass']( this.pastHeroClass );

		if ( ! this.isMobile ) {

			this.toggleNavVisibility( ! toStick, false, null );
		}
	}

	/**
	 * Opens and closes nav
	 */
	toggleNav( toOpen = true, animate = true, onComplete = null ) {

		this.isNavOpen = toOpen;

		const classFunction = toOpen
			? 'addClass'
			: 'removeClass';

		const aria = toOpen
			? 'true'
			: 'false';

		this.hamburger[classFunction]( this.toggledClass )
			.attr( 'aria-expanded', aria );

		this.headerNavigation[classFunction]( this.toggledClass )
			.attr( 'aria-expanded', aria );

		jQuery( 'html, body' )[classFunction]( this.hasToggledNavClass );

		if ( this.isMobile ) {
			gsap.set( window, {	scrollTo: {	y: 0 } } );
		}

		this.toggleNavVisibility( toOpen, animate, onComplete );
	}

	toggleNavVisibility( toVisible = true, animate = true, onComplete = null ) {

		// Greensock is wonderful

		gsap[animate ? 'to' : 'set'](
			this.headerNavigation,
			{
				duration: this.navAnimationDuration,
				opacity: toVisible ? 1 : 0,
				x: toVisible ? 0 : this.navTransform,
				onComplete: onComplete
			}
		);

	}

	/**
	 * Resets the subnav.
	 * Typically used when something is resized.
	 */
	reset( elements = ['anchors', 'hero', 'intrusiveDarkSections'] ) {

		if ( ! this.isMobile ) {

			if ( ! this.isListeningForScroll ) {
				this.listenForScroll( true );
			}
		}

		else {
			this.listenForScroll( false );
		}

		this.getOffsets( elements );
		this.handleScroll();
	}

	/* Helper methods --------------------------------------- */

	/**
	 * Gets the window eye level.
	 */
	getWindowEyeLevel( windowTop = null, windowHeight = null ) {

		windowTop = windowTop || window.pageYOffset;
		windowHeight = windowHeight || window.innerHeight;

		const windowEyeLevelTopRatio = 0.4;
		const windowEyeLevelTop = windowHeight * windowEyeLevelTopRatio;

		return {
			viewport: windowEyeLevelTop,
			document: windowTop + windowEyeLevelTop
		};
	}

}

const subnav = new DispatchSkinSubnav();
export default subnav;



// WEBPACK FOOTER //
// ./src/js/inc/subnav.js